import React, { useEffect, useState, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { hotels, destinations } from '../data';
import { Check, ArrowLeft, ArrowRight, Info, Calendar, MapPin, Star, Grid, Maximize2, X, ChevronLeft, ChevronRight, Utensils, HeartPulse, Pill, Plane, Waves, ExternalLink, Sparkles } from 'lucide-react';
import SEO from '../components/SEO';
import { useLanguage } from '../components/LanguageContext';
import { hotelInspectionsMap } from '../hotelInspectionsData';
import { buildHotelSchema, buildBreadcrumbSchema, buildOrganizationSchema, getOptimizedImageUrl } from '../lib/schema';

const hotelAccessibilityIcons = [
  {
    id: 'liftbus',
    label: 'Liftbus',
    label_en: 'Lift bus',
    description: 'Transport med liftbus kan arrangeres efter behov.',
    description_en: 'Transport with wheelchair accessible bus can be arranged as needed.',
    src: 'https://assets.auxodev.dk/assets/1a80aa53-8838-4db9-a7e2-061c5ee04d6a/16e67773-17d1-4977-8ebc-6d4de0926454.png',
  },
  {
    id: 'lufthavnsassistance',
    label: 'Lufthavnsassistance',
    label_en: 'Airport assistance',
    description: 'Assistance i lufthavnen gør rejsen lettere fra start til slut.',
    description_en: 'Assistance at the airport makes the journey easier from start to finish.',
    src: 'https://assets.auxodev.dk/assets/1a80aa53-8838-4db9-a7e2-061c5ee04d6a/3c0659b6-0083-4213-848c-2062524f3616.png',
  },
  {
    id: 'vaerelse',
    label: 'Handicapvenligt værelse',
    label_en: 'Wheelchair friendly room',
    description: 'Værelser med bedre plads og tilpassede adgangsforhold.',
    description_en: 'Rooms with more space and adapted access conditions.',
    src: 'https://assets.auxodev.dk/assets/1a80aa53-8838-4db9-a7e2-061c5ee04d6a/f48fcfaf-2f22-4210-9bb3-75fc4409775f.png',
  },
  {
    id: 'elevator',
    label: 'Elevator',
    label_en: 'Elevator',
    description: 'Elevatoradgang til relevante etager og hotelområder.',
    description_en: 'Elevator access to relevant floors and hotel areas.',
    src: 'https://assets.auxodev.dk/assets/1a80aa53-8838-4db9-a7e2-061c5ee04d6a/2cd1f690-a651-44c3-b2d1-804cd931b3da.png',
  },
  {
    id: 'terraen',
    label: 'Fladt terræn',
    label_en: 'Flat terrain',
    description: 'Området er velegnet, når niveaufri adgang er vigtig.',
    description_en: 'The area is suitable when level access is important.',
    src: 'https://assets.auxodev.dk/assets/1a80aa53-8838-4db9-a7e2-061c5ee04d6a/231264bb-5938-43d8-b180-3f4718d662f7.png',
  },
  {
    id: 'badestol',
    label: 'Roll-in shower og badestol',
    label_en: 'Roll-in shower & shower chair',
    description: 'Badeværelse med roll-in shower og mulighed for badestol.',
    description_en: 'Bathroom with roll-in shower and option for a shower chair.',
    src: 'https://assets.auxodev.dk/assets/1a80aa53-8838-4db9-a7e2-061c5ee04d6a/642bc099-a4a0-4d02-8230-d7eaddbaa956.png',
  },
  {
    id: 'greb',
    label: 'Støttehåndtag',
    label_en: 'Grab rails',
    description: 'Støttehåndtag på badeværelset giver ekstra tryghed.',
    description_en: 'Grab rails in the bathroom provide extra safety.',
    src: 'https://assets.auxodev.dk/assets/1a80aa53-8838-4db9-a7e2-061c5ee04d6a/61814ecd-ef7d-4d64-9da9-db55ca3ff8b9.png',
  },
  {
    id: 'pool_lift',
    label: 'Poollift',
    label_en: 'Pool lift',
    description: 'Poollift gør det lettere at komme sikkert i og op af poolen.',
    description_en: 'Pool lift makes it easier to get safely in and out of the pool.',
    src: 'https://assets.auxodev.dk/assets/1a80aa53-8838-4db9-a7e2-061c5ee04d6a/095dfd29-3996-40cf-9dca-04c298f03c83.png',
  },
  {
    id: 'romertrappe',
    label: 'Romertrappe',
    label_en: 'Roman stairs',
    description: 'Pool med romertrappe og gelænder, som gør det nemmere at komme i vandet.',
    description_en: 'Pool with Roman stairs and handrail for easier access into the water.',
    src: 'https://assets.auxodev.dk/assets/1a80aa53-8838-4db9-a7e2-061c5ee04d6a/3c387803-91e8-4348-95b0-45f953c145ed.png',
  },
  {
    id: 'strand',
    label: 'Handicapvenlig strand',
    label_en: 'Accessible beach',
    description: 'Strandforhold med bedre adgang for gangbesværede og kørestolsbrugere.',
    description_en: 'Beach conditions with better access for people with walking difficulties and wheelchair users.',
    src: 'https://assets.auxodev.dk/assets/1a80aa53-8838-4db9-a7e2-061c5ee04d6a/c9503ed9-3f4e-43d5-9669-bf34f52bd1b6.png',
  },
  {
    id: 'parkering',
    label: 'Handicap parkering',
    label_en: 'Disabled parking',
    description: 'Parkering tæt på indgangen eller med tilpasset adgang.',
    description_en: 'Parking close to the entrance or with adapted access.',
    src: 'https://assets.auxodev.dk/assets/1a80aa53-8838-4db9-a7e2-061c5ee04d6a/80d7de80-28ce-470e-b9a3-7686fe2b5464.png',
  },
  {
    id: 'opvarmet_pool',
    label: 'Udendørs opvarmet pool',
    label_en: 'Outdoor heated pool',
    description: 'Udendørs opvarmet pool delvis tilgængelig med handicap toilet',
    description_en: 'Outdoor heated pool partially accessible with disabled toilet',
    src: 'https://assets.auxodev.dk/assets/1a80aa53-8838-4db9-a7e2-061c5ee04d6a/22aac766-ddb7-4e8b-870d-50062b18b404.png',
  },
  {
    id: 'indendoers_pool',
    label: 'Indendørs pool',
    label_en: 'Indoor pool',
    description: 'Indendørs swimmingpool i hotellets wellnessområde.',
    description_en: 'Indoor swimming pool in the hotel wellness area.',
    src: `data:image/svg+xml;utf8,${encodeURIComponent(`
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" fill="none" stroke="#004F3B" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round">
        <!-- Tag med udhæng for indendørs bygning -->
        <path d="M16 40 L50 18 L84 40" />
        <!-- Bygningens vægge med bløde hjørner -->
        <path d="M22 40 V72 C22 74.5 24 76 26.5 76 H73.5 C76 76 78 74.5 78 72 V40" />
        <!-- Rolige vandbølger i poolen -->
        <path d="M28 64 Q35 59 42 64 T56 64 T70 64" />
        <!-- Poolstige med buet gelænder foroven -->
        <path d="M60 48 C60 44 63 44 63 48 V65" />
        <path d="M68 48 C68 44 71 44 71 48 V65" />
        <path d="M63 53 H68" />
        <path d="M63 59 H68" />
      </svg>
    `)}`,
  },
  {
    id: 'bagageopbevaring',
    label: 'Gratis bagageopbevaring',
    label_en: 'Free luggage storage',
    description: 'Gratis bagage opbevaring',
    description_en: 'Free luggage storage',
    src: 'https://assets.auxodev.dk/assets/1a80aa53-8838-4db9-a7e2-061c5ee04d6a/9aa6bd2d-0d72-4008-84cd-5bade46f88a5.png',
  },
  {
    id: 'naer_tilgaengelig_station',
    label: 'Nær tilgængelig station',
    label_en: 'Near accessible station',
    description: 'Nær tilgængelig station',
    description_en: 'Near accessible station',
    src: 'https://assets.auxodev.dk/assets/1a80aa53-8838-4db9-a7e2-061c5ee04d6a/18ceeb1c-731e-4d43-b1d3-9c6b754184ce.png',
  },
  {
    id: 'vaskefaciliteter',
    label: 'Gratis tøjvask',
    label_en: 'Free laundry',
    description: 'Gratis tøjvask',
    description_en: 'Free laundry',
    src: 'https://assets.auxodev.dk/assets/1a80aa53-8838-4db9-a7e2-061c5ee04d6a/36c9c32d-7e2c-440c-9735-18fe7034de3a.png',
  }
];

const getTooltipPositionClasses = (index) => {
  const colMobile = index % 4;
  const colSmall = index % 6;
  const colLarge = index % 11;

  let mobileClass = 'left-1/2 -translate-x-1/2';
  if (colMobile === 0) mobileClass = 'left-0 translate-x-0';
  if (colMobile === 3) mobileClass = 'right-0 left-auto translate-x-0';

  let smallClass = 'sm:left-1/2 sm:-translate-x-1/2 sm:right-auto';
  if (colSmall === 0) smallClass = 'sm:left-0 sm:translate-x-0 sm:right-auto';
  if (colSmall === 5) smallClass = 'sm:right-0 sm:left-auto sm:translate-x-0';

  let largeClass = 'xl:left-1/2 xl:-translate-x-1/2 xl:right-auto';
  if (colLarge === 0 || colLarge === 1) largeClass = 'xl:left-0 xl:translate-x-0 xl:right-auto';
  if (colLarge === 9 || colLarge === 10) largeClass = 'xl:right-0 xl:left-auto xl:translate-x-0';

  return `${mobileClass} ${smallClass} ${largeClass}`;
};

const getTooltipArrowPositionClasses = (index) => {
  const colMobile = index % 4;
  const colSmall = index % 6;
  const colLarge = index % 11;

  let mobileArrow = 'left-1/2';
  if (colMobile === 0) mobileArrow = 'left-6';
  if (colMobile === 3) mobileArrow = 'left-auto right-6';

  let smallArrow = 'sm:left-1/2 sm:right-auto';
  if (colSmall === 0) smallArrow = 'sm:left-8 sm:right-auto';
  if (colSmall === 5) smallArrow = 'sm:left-auto sm:right-8';

  let largeArrow = 'xl:left-1/2 xl:right-auto';
  if (colLarge === 0) largeArrow = 'xl:left-8 xl:right-auto';
  if (colLarge === 1) largeArrow = 'xl:left-16 xl:right-auto';
  if (colLarge === 9) largeArrow = 'xl:left-auto xl:right-16';
  if (colLarge === 10) largeArrow = 'xl:left-auto xl:right-8';

  return `${mobileArrow} ${smallArrow} ${largeArrow}`;
};

const HotelAccessibilityIcon = ({ icon, index }) => {
  const { language } = useLanguage();
  const label = language === 'en' && icon.label_en ? icon.label_en : icon.label;
  const description = language === 'en' && icon.description_en ? icon.description_en : icon.description;
  const tooltipPos = getTooltipPositionClasses(index);
  const arrowPos = getTooltipArrowPositionClasses(index);

  return (
    <div className="group relative flex flex-col items-center justify-start text-center h-full" tabIndex={0} aria-label={`${label}: ${description}`}>
      <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-[#004F3B]/5 ring-1 ring-[#004F3B]/10 flex items-center justify-center p-2.5 transition-transform duration-200 group-hover:scale-105 group-focus:scale-105 cursor-pointer shrink-0">
        <img 
          src={icon.src?.startsWith('data:') ? icon.src : getOptimizedImageUrl(icon.src, 120, 85)} 
          alt="" 
          className="w-full h-full object-contain" 
          loading="lazy"
          style={icon.src?.startsWith('data:') ? undefined : {
            filter: 'invert(18%) sepia(91%) saturate(1414%) hue-rotate(134deg) brightness(93%) contrast(101%)'
          }}
        />
      </div>
      <span className="text-[11px] text-gray-700 mt-2 font-semibold block text-center w-full leading-tight">
        {label}
      </span>
      <div className={`pointer-events-none absolute top-full z-20 mt-3 w-52 rounded-xl bg-gray-900 px-4 py-3 text-left text-xs text-white opacity-0 shadow-xl shadow-gray-900/20 transition-all duration-200 group-hover:translate-y-1 group-hover:opacity-100 group-focus:translate-y-1 group-focus:opacity-100 ${tooltipPos}`}>
        <p className="font-bold uppercase tracking-wide text-[10px]">{label}</p>
        <p className="mt-1 leading-relaxed text-gray-200 text-[11px]">{description}</p>
        <span className={`absolute -top-1.5 h-3 w-3 -translate-x-1/2 rotate-45 bg-gray-900 ${arrowPos}`} />
      </div>
    </div>
  );
};

const renderDescriptionWithLinks = (text) => {
  if (!text) return null;
  const cleanText = text.replace(/(^|\n)###\s*/g, '$1');

  const parseBold = (str) => {
    if (!str || typeof str !== 'string' || !str.includes('**')) return str;
    const parts = str.split(/(\*\*.*?\*\*)/g);
    return parts.map((part, i) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return (
          <strong key={i} className="font-bold text-gray-900">
            {part.slice(2, -2)}
          </strong>
        );
      }
      return part;
    });
  };

  const parseSonnblick = (str) => {
    if (typeof str === 'string' && str.includes("Sonnblickbad Rauris")) {
      const parts = str.split("Sonnblickbad Rauris");
      return (
        <>
          {parseBold(parts[0])}
          <a 
            href="https://www.nationalpark-sommercard.at/en/service/partner/SonnblickbadRauris~20616" 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-emerald-700 hover:underline font-bold inline-flex items-center gap-0.5"
          >
            Sonnblickbad Rauris
            <ExternalLink size={14} className="inline" />
          </a>
          {parseBold(parts[1])}
        </>
      );
    }
    return parseBold(str);
  };

  // Parse markdown links [text](url)
  const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
  if (linkRegex.test(cleanText)) {
    const segments = [];
    let lastIndex = 0;
    linkRegex.lastIndex = 0;
    let match;
    let keyIdx = 0;

    while ((match = linkRegex.exec(cleanText)) !== null) {
      if (match.index > lastIndex) {
        segments.push(
          <React.Fragment key={`txt-${keyIdx++}`}>
            {parseSonnblick(cleanText.substring(lastIndex, match.index))}
          </React.Fragment>
        );
      }
      const label = match[1];
      const href = match[2];
      const isInternal = href.startsWith('/');

      if (isInternal) {
        segments.push(
          <Link
            key={`lnk-${keyIdx++}`}
            to={href}
            className="text-emerald-800 hover:text-emerald-950 font-bold underline inline-flex items-center gap-1 transition-colors"
          >
            <span>{parseBold(label)}</span>
            <ArrowRight size={13} className="inline shrink-0" />
          </Link>
        );
      } else {
        segments.push(
          <a
            key={`lnk-${keyIdx++}`}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="text-emerald-700 hover:underline font-bold inline-flex items-center gap-0.5"
          >
            <span>{parseBold(label)}</span>
            <ExternalLink size={14} className="inline shrink-0" />
          </a>
        );
      }
      lastIndex = linkRegex.lastIndex;
    }

    if (lastIndex < cleanText.length) {
      segments.push(
        <React.Fragment key={`txt-${keyIdx++}`}>
          {parseSonnblick(cleanText.substring(lastIndex))}
        </React.Fragment>
      );
    }
    return segments;
  }

  return parseSonnblick(cleanText);
};

const translateMonth = (month, lang) => {
  if (lang === 'da') return month;
  const monthsMap = {
    januar: 'January',
    februar: 'February',
    marts: 'March',
    april: 'April',
    maj: 'May',
    juni: 'June',
    juli: 'July',
    august: 'August',
    september: 'September',
    oktober: 'October',
    november: 'November',
    december: 'December'
  };
  return monthsMap[month.toLowerCase()] || month;
};

const getHotelMapEmbedUrl = (hotel, destination) => {
  if (hotel?.mapEmbedUrl) {
    return hotel.mapEmbedUrl;
  }
  if (hotel?.mapQuery) {
    return `https://www.google.com/maps/embed?origin=mfe&pb=!1m2!2m1!1s${encodeURIComponent(hotel.mapQuery)}`;
  }
  const query = [hotel?.name, hotel?.area, destination?.title].filter(Boolean).join(', ');
  return `https://www.google.com/maps/embed?origin=mfe&pb=!1m2!2m1!1s${encodeURIComponent(query)}`;
};

const HotelDetailPage = () => {
  const { language, t, translateObject, getLocalizedPath } = useLanguage();
  const { hotelId } = useParams();
  const rawHotel = hotels.find(h => h.id === hotelId);
  const hotel = translateObject(rawHotel);
  
  const [selectedImage, setSelectedImage] = useState(null);
  const [showAllImages, setShowAllImages] = useState(false);
  const scrollContainerRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [hotelId]);

  // Keyboard navigation for Lightbox
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (selectedImage === null || !hotel) return;
      if (e.key === 'ArrowRight') {
        setSelectedImage((prev) => (prev + 1) % hotel.gallery.length);
      } else if (e.key === 'ArrowLeft') {
        setSelectedImage((prev) => (prev - 1 + hotel.gallery.length) % hotel.gallery.length);
      } else if (e.key === 'Escape') {
        setSelectedImage(null);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedImage, hotel]);

  useEffect(() => {
    if (selectedImage !== null) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [selectedImage]);

  if (!hotel) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4">
            {language === 'da' ? 'Hotellet blev ikke fundet' : 'Hotel not found'}
          </h2>
          <Link to={getLocalizedPath('destinations', language)} className="text-emerald-700 hover:underline">
            {language === 'da' ? 'Gå tilbage til destinationer' : 'Go back to destinations'}
          </Link>
        </div>
      </div>
    );
  }

  const rawDestination = destinations.find(d => d.id === hotel.destinationId);
  const destination = translateObject(rawDestination);
  const visibleImages = showAllImages ? hotel.gallery : hotel.gallery.slice(0, 8);
  const hotelDetailIntro = hotel.detailIntro || hotel.description;

  const openLightbox = (index) => {
    setSelectedImage(index);
  };

  const closeLightbox = () => {
    setSelectedImage(null);
  };

  const nextImage = (e) => {
    e.stopPropagation();
    setSelectedImage((prev) => (prev + 1) % hotel.gallery.length);
  };

  const prevImage = (e) => {
    e.stopPropagation();
    setSelectedImage((prev) => (prev - 1 + hotel.gallery.length) % hotel.gallery.length);
  };

  const scroll = (direction) => {
    if (scrollContainerRef.current) {
      const scrollAmount = direction === 'left' ? -500 : 500;
      scrollContainerRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  };

  const hotelCanonicalPath = getLocalizedPath('hotel', language, { hotelId: hotel.id });
  const breadcrumbs = [
    {
      name: language === 'da' ? 'Forside' : 'Home',
      url: getLocalizedPath('home', language)
    },
    ...(destination ? [{
      name: destination.title,
      url: getLocalizedPath('destination', language, { id: hotel.destinationId })
    }] : [{
      name: language === 'da' ? 'Handicapvenlige hoteller' : 'Accessible hotels',
      url: getLocalizedPath('hotels', language)
    }]),
    {
      name: hotel.name,
      url: hotelCanonicalPath
    }
  ];

  const hotelSchema = buildHotelSchema({
    hotel,
    destinationTitle: destination?.title,
    description: hotelDetailIntro,
    url: hotelCanonicalPath,
    language
  });

  const breadcrumbSchema = buildBreadcrumbSchema(breadcrumbs, hotelCanonicalPath);

  const organizationSchema = buildOrganizationSchema({ language, asTravelAgency: false });

  return (
    <div className="pt-24 md:pt-28 overflow-x-hidden w-full">
      <SEO 
        title={language === 'da' ? `Handicapvenligt hotel: ${hotel.name} i ${hotel.area}` : `Wheelchair friendly hotel: ${hotel.name} in ${hotel.area}`}
        description={language === 'da' 
          ? `${hotelDetailIntro} Oplev en tryg, tilgængelig og handicapvenlig ferie i ${hotel.area}, ${destination?.title || ''} med Alora Rejser.`
          : `${hotelDetailIntro} Experience a safe, accessible and wheelchair friendly holiday in ${hotel.area}, ${destination?.title || ''} with Alora Rejser.`
        }
        ogImage={hotel.image}
        schemaMarkup={[organizationSchema, hotelSchema, breadcrumbSchema]}
      />
      {/* Hero / Gallery Section */}
      <section className="bg-gray-50 overflow-hidden w-full">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 w-full">
          <nav className="mb-6">
            <Link to={getLocalizedPath('destination', language, { id: hotel.destinationId })} className="inline-flex items-center text-emerald-700 hover:text-emerald-800 font-medium transition-colors">
              <ArrowLeft className="w-4 h-4 mr-2" />
              {language === 'da' ? `Tilbage til ${destination?.title || 'destination'}` : `Back to ${destination?.title || 'destination'}`}
            </Link>
          </nav>

          {/* Premium Horizontal Scroll Gallery */}
          <div className="relative group/gallery overflow-hidden rounded-2xl">
            <style>{`
              .no-scrollbar::-webkit-scrollbar {
                display: none;
              }
              .no-scrollbar {
                -ms-overflow-style: none;
                scrollbar-width: none;
              }
            `}</style>
            
            {/* Scroll Container */}
            <div 
              ref={scrollContainerRef}
              className="no-scrollbar flex gap-4 overflow-x-auto scroll-smooth snap-x snap-mandatory h-[350px] md:h-[480px] lg:h-[520px] rounded-2xl py-1 px-1 max-w-full"
            >
              {hotel.isNew && (
                <div className="absolute top-4 left-4 z-20 bg-gradient-to-r from-amber-500 via-amber-600 to-amber-700 text-white font-black text-xs md:text-sm uppercase px-3.5 py-1.5 md:px-4 md:py-2 rounded-full shadow-xl tracking-widest flex items-center gap-2 border border-amber-200/50 animate-pulse max-w-[calc(100%-2rem)]">
                  <Sparkles size={16} className="fill-white shrink-0" />
                  <span className="truncate">{language === 'da' ? 'EN HELT FANTASTISK NYHED' : 'A FANTASTIC NEW ADDITION'}</span>
                </div>
              )}
              {/* Combine main image and gallery to show everything in order */}
              {hotel.gallery.map((img, index) => (
                <div 
                  key={index}
                  onClick={() => openLightbox(index)}
                  className="relative h-full w-auto flex-shrink-0 snap-start rounded-2xl overflow-hidden shadow-md cursor-pointer group/img bg-stone-100 border border-stone-200/40"
                >
                  <img 
                    src={getOptimizedImageUrl(img, 800, 80)} 
                    alt={`${hotel.name} - Billede ${index + 1}`} 
                    className="h-full w-auto object-contain rounded-2xl transition-transform duration-500 group-hover/img:scale-[1.01]"
                    loading={index < 2 ? "eager" : "lazy"}
                    decoding="async"
                  />
                  {/* Subtle overlay on hover */}
                  <div className="absolute inset-0 bg-black/0 group-hover/img:bg-black/20 transition-colors duration-300 flex items-center justify-center">
                    <span className="w-12 h-12 rounded-full bg-white/25 backdrop-blur-md text-white border border-white/30 shadow-lg flex items-center justify-center opacity-0 group-hover/img:opacity-100 transform scale-90 group-hover/img:scale-100 transition-all duration-300 hover:bg-white hover:text-emerald-950 hover:scale-110">
                      <Maximize2 className="w-5 h-5 stroke-[1.5]" />
                    </span>
                  </div>
                </div>
              ))}
            </div>

            {/* Navigation Arrows */}
            <button 
              onClick={() => scroll('left')}
              className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/95 hover:bg-white text-gray-800 hover:text-emerald-800 p-3.5 rounded-full shadow-xl border border-gray-200/50 transition-all hover:scale-110 cursor-pointer z-10 opacity-0 group-hover/gallery:opacity-100 focus:opacity-100"
              aria-label={language === 'da' ? "Rul til venstre" : "Scroll left"}
            >
              <ChevronLeft size={24} />
            </button>

            <button 
              onClick={() => scroll('right')}
              className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/95 hover:bg-white text-gray-800 hover:text-emerald-800 p-3.5 rounded-full shadow-xl border border-gray-200/50 transition-all hover:scale-110 cursor-pointer z-10 opacity-0 group-hover/gallery:opacity-100 focus:opacity-100"
              aria-label={language === 'da' ? "Rul til højre" : "Scroll right"}
            >
              <ChevronRight size={24} />
            </button>

            {/* Floating Info Badge / Scroll Indicator */}
            <div className="hidden sm:flex absolute bottom-4 left-4 bg-black/60 backdrop-blur-md text-white px-4 py-2 rounded-xl text-xs font-medium items-center gap-2 pointer-events-none shadow-md max-w-[calc(100%-140px)]">
              <span className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse shrink-0" />
              <span className="truncate">
                {language === 'da' 
                  ? `Rul eller brug pilene til at se alle ${hotel.gallery.length} billeder` 
                  : `Scroll or use the arrows to see all ${hotel.gallery.length} images`
                }
              </span>
            </div>

            {/* "Vis alle billeder" Button */}
            <button
              onClick={() => openLightbox(0)}
              className="absolute bottom-4 right-4 bg-white/95 hover:bg-white text-gray-900 px-4 py-2.5 rounded-xl font-bold text-sm shadow-md hover:shadow-lg transition-all flex items-center gap-2 border border-gray-200/50 cursor-pointer z-10"
            >
              <Grid className="w-4 h-4 text-emerald-800" />
              <span>{language === 'da' ? 'Åbn galleri' : 'Open gallery'}</span>
            </button>
          </div>
        </div>
      </section>

      {/* Hotel Info Section */}
      <section className="py-12 bg-white overflow-hidden w-full">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 w-full">
            
            {/* Main Content */}
            <div className="lg:col-span-2 min-w-0 w-full">
              <div className="flex flex-wrap items-center gap-3 mb-4">
                {hotel.isNew && (
                  <span className="bg-gradient-to-r from-amber-500 to-amber-600 text-white font-black text-xs uppercase px-3.5 py-1 rounded-full shadow-md tracking-widest flex items-center gap-1.5 border border-amber-300/40 animate-pulse">
                    <Sparkles size={13} className="fill-white shrink-0" />
                    {language === 'da' ? 'NYHED' : 'NEW'}
                  </span>
                )}
                <span className="bg-emerald-100 text-emerald-800 text-xs font-bold px-2.5 py-0.5 rounded uppercase tracking-wider">
                  {hotel.area}
                </span>
                <div className="flex text-yellow-400">
                  {[...Array(hotel.stars || 0)].map((_, i) => <Star key={i} className="w-4 h-4 fill-current" />)}
                </div>
              </div>
              
              <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 break-words">{hotel.name}</h1>
              
              <div className="prose prose-lg text-gray-600 max-w-none mb-8 break-words [overflow-wrap:anywhere]">
                <p className="font-medium text-gray-900 mb-4 break-words">{hotelDetailIntro}</p>
                <p className="whitespace-pre-line leading-relaxed break-words [overflow-wrap:anywhere]">{renderDescriptionWithLinks(hotel.longDescription)}</p>
              </div>

              {/* Link til hotelinspektion på rejsetips hvis tilgængelig */}
              {hotelInspectionsMap[hotel.id] ? (
                <div className="mb-8 p-5 md:p-6 bg-stone-50/90 rounded-2xl border border-stone-200/80 flex flex-col md:flex-row md:items-center justify-between gap-5 transition-colors">
                  <div className="flex items-start gap-3.5">
                    <div className="w-10 h-10 rounded-xl bg-[#99A18C]/15 text-[#6c7460] flex items-center justify-center shrink-0 mt-0.5">
                      <Star className="w-5 h-5 fill-[#99A18C]/40 text-[#6c7460]" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-[11px] font-semibold text-[#6c7460] uppercase tracking-wider">
                          {language === 'da' ? 'Personlig inspektion' : 'Personal inspection'}
                        </span>
                      </div>
                      <h4 className="text-base md:text-lg font-bold text-slate-900">
                        {language === 'da' ? `Aloras inspektion af ${hotel.name}` : `Alora's inspection of ${hotel.name}`}
                      </h4>
                      <p className="text-xs md:text-sm text-slate-600 mt-1 leading-relaxed max-w-2xl">
                        {language === 'da'
                          ? 'Læs vores personlige erfaringer, ærlige vurdering og tilgængelighedsdetaljer fra vores eget besøg under rejsetips.'
                          : 'Read our personal experience, honest review and accessibility details from our own visit under travel tips.'}
                      </p>
                    </div>
                  </div>
                  <Link
                    to={`/${language}/${language === 'en' ? 'travel-tips' : 'rejsetips'}?article=${hotelInspectionsMap[hotel.id]}`}
                    className="inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-white hover:bg-stone-100 text-slate-800 text-xs md:text-sm font-semibold rounded-xl border border-stone-300 transition-all shrink-0 shadow-2xs hover:border-stone-400"
                  >
                    <span>{language === 'da' ? 'Læs inspektionen her' : 'Read inspection here'}</span>
                    <ArrowRight size={14} className="text-[#99A18C]" />
                  </Link>
                </div>
              ) : (
                <div className="mb-8 p-5 md:p-6 bg-stone-50/90 rounded-2xl border border-stone-200/80 flex flex-col md:flex-row md:items-center justify-between gap-5 transition-colors">
                  <div className="flex items-start gap-3.5">
                    <div className="w-10 h-10 rounded-xl bg-[#99A18C]/15 text-[#6c7460] flex items-center justify-center shrink-0 mt-0.5">
                      <Star className="w-5 h-5 fill-[#99A18C]/40 text-[#6c7460]" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-[11px] font-semibold text-[#6c7460] uppercase tracking-wider">
                          {language === 'da' ? 'Rejsetips & Erfaringer' : 'Travel tips & insights'}
                        </span>
                      </div>
                      <h4 className="text-base md:text-lg font-bold text-slate-900">
                        {language === 'da' ? 'Få personlige råd og rejsetips' : 'Get personal advice and travel tips'}
                      </h4>
                      <p className="text-xs md:text-sm text-slate-600 mt-1 leading-relaxed max-w-2xl">
                        {language === 'da'
                          ? 'Læs vores personlige erfaringer, gode råd og tilgængelighedsdetaljer fra vores egne rejser under rejsetips.'
                          : 'Read our personal experience, practical advice and accessibility details from our own travels under travel tips.'}
                      </p>
                    </div>
                  </div>
                  <Link
                    to={`/${language}/${language === 'en' ? 'travel-tips' : 'rejsetips'}`}
                    className="inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-white hover:bg-stone-100 text-slate-800 text-xs md:text-sm font-semibold rounded-xl border border-stone-300 transition-all shrink-0 shadow-2xs hover:border-stone-400"
                  >
                    <span>{language === 'da' ? 'Læs rejsetips her' : 'Read travel tips here'}</span>
                    <ArrowRight size={14} className="text-[#99A18C]" />
                  </Link>
                </div>
              )}

              <div className="mb-10 space-y-3">
                <div className="flex items-center justify-between gap-4">
                  <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-[#99A18C]">
                    {hotel.isApproximateLocation 
                      ? (language === 'da' ? 'Villaens placering' : 'Villa location')
                      : (language === 'da' ? 'Hotellets placering' : 'Hotel location')}
                  </h3>
                  <span className="text-[11px] text-slate-500">
                    {hotel.isApproximateLocation 
                      ? (language === 'da' ? 'Omtrentligt villaområde' : 'Approximate area')
                      : (language === 'da' ? 'Vises som kort over området' : 'Shown as a map of the area')}
                  </span>
                </div>
                <div className="overflow-hidden rounded-[1.75rem] border border-stone-200 bg-stone-50 shadow-sm relative h-[250px] md:h-[290px]">
                  <iframe
                    title={language === 'da' ? `Kort over ${hotel.name}` : `Map of ${hotel.name}`}
                    src={getHotelMapEmbedUrl(hotel, destination)}
                    className="absolute left-0 top-[-80px] w-full h-[calc(100%+80px)]"
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  />
                  {hotel.isApproximateLocation && (
                    <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
                      <div className="relative flex flex-col items-center justify-center">
                        <div className="w-32 h-32 md:w-44 md:h-44 rounded-full border-2 border-dashed border-emerald-600/90 bg-emerald-500/20 backdrop-blur-[0.5px] shadow-lg animate-pulse flex items-center justify-center">
                          <div className="w-14 h-14 rounded-full border border-emerald-700/60 bg-emerald-600/30" />
                        </div>
                        <div className="mt-2.5 bg-white/95 backdrop-blur-md text-emerald-950 px-3 py-1 rounded-full text-xs font-bold shadow-md border border-emerald-200 flex items-center gap-1.5">
                          <span className="w-2 h-2 rounded-full bg-emerald-600 animate-ping"></span>
                          <span>{language === 'da' ? 'Ca. placering (2 km nord for Trogir)' : 'Approx. area (2 km north of Trogir)'}</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                {hotel.isApproximateLocation && (
                  <p className="text-xs text-stone-500 italic mt-2">
                    {language === 'da' 
                      ? 'ℹ️ Bemærk: Villaen er beliggende ca. 2 km nord for Trogir mod kysten. Af hensyn til udlejer og diskretion vises kun en omtrentlig placering på kortet. Den nøjagtige adresse udleveres personligt ved booking.'
                      : 'ℹ️ Note: The villa is located approx. 2 km north of Trogir towards the coast. For privacy reasons, only the approximate area is indicated on the map. The exact address is provided upon booking.'}
                  </p>
                )}
              </div>

              {/* Flot link til turforslag under hotelbeskrivelsen */}
              <div className="mb-10 p-6 md:p-8 bg-emerald-50 rounded-3xl border border-emerald-100/80 flex flex-col md:flex-row md:items-center justify-between gap-6 shadow-sm">
                <div className="flex items-start gap-4">
                  <span className="text-3xl md:text-4xl mt-1">🗺️</span>
                  <div>
                    <h4 className="font-serif text-xl md:text-2xl font-bold text-emerald-900">
                      {language === 'da' ? 'Lokale turforslag & oplevelser' : 'Local tour suggestions & experiences'}
                    </h4>
                    <p className="text-sm md:text-base text-slate-700 mt-1.5 leading-relaxed">
                      {language === 'da' 
                        ? 'Se vores håndplukkede forslag til tilgængelige ture og seværdigheder i området.' 
                        : 'See our handpicked suggestions for accessible tours and sights in the area.'
                      }
                    </p>
                  </div>
                </div>
                <Link 
                  to={getLocalizedPath('hotel-tour', language, { hotelId: hotel.id })}
                  className="inline-flex items-center justify-center px-6 py-3.5 bg-emerald-800 hover:bg-emerald-900 text-white text-sm md:text-base font-bold rounded-xl transition-all shrink-0 shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
                >
                  {language === 'da' ? 'Se turforslag' : 'See tour suggestions'}
                </Link>
              </div>

              {/* Kort og Nationalpark Sommercard sektion for Appartementhaus Renate */}
              {hotel.id === 'appartementhaus-renate' && (
                <div className="mb-10 p-6 md:p-8 bg-stone-50 rounded-3xl border border-stone-200/60 space-y-6">
                  <div className="flex flex-col md:flex-row gap-6 items-center">
                    <div className="md:w-1/2 space-y-4">
                      <span className="inline-block px-3 py-1 bg-emerald-100 text-emerald-800 rounded-full text-xs font-bold uppercase tracking-wider">
                        {language === 'da' ? 'Inkluderet i opholdet' : 'Included in the stay'}
                      </span>
                      <h3 className="text-2xl font-serif font-bold text-emerald-900 italic">
                        Nationalpark Sommercard
                      </h3>
                      <p className="text-sm text-slate-600 leading-relaxed">
                        {language === 'da' ? (
                          <>Når du bor på <strong>Appartementhaus Renate</strong> i Rauris, får du det fantastiske <strong>Nationalpark Sommercard</strong> med i dit ophold helt uden ekstra beregning. Kortet giver dig gratis adgang eller store rabatter til over 60 spændende attraktioner i regionen, herunder gratis ture med de lokale bjergbaner og lifte, adgang til svømmehaller, museer samt gratis brug af offentlig transport.</>
                        ) : (
                          <>When you stay at <strong>Appartementhaus Renate</strong> in Rauris, you get the fantastic <strong>Nationalpark Sommercard</strong> included in your stay at no extra cost. The card gives you free access or large discounts to over 60 exciting attractions in the region, including free trips with the local mountain railways and lifts, access to swimming pools, museums, as well as free use of public transport.</>
                        )}
                      </p>
                      <a 
                        href="https://www.raurisertal.at/en/summer/nationalpark-summercard" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-5 py-2.5 bg-emerald-800 hover:bg-emerald-900 text-white text-xs font-bold rounded-xl transition-all shadow-sm"
                      >
                        {language === 'da' ? 'Læs mere om Sommercard her' : 'Read more about Sommercard here'}
                        <ExternalLink size={14} />
                      </a>
                    </div>
                    <div className="md:w-1/2 w-full">
                      <div className="relative rounded-2xl overflow-hidden border border-stone-200 shadow-md bg-white group/map cursor-pointer" onClick={() => window.open('https://assets.auxodev.dk/assets/1a80aa53-8838-4db9-a7e2-061c5ee04d6a/158df1e9-8230-41a7-a84b-38b95d04c27f.png', '_blank')}>
                        <img 
                          src={getOptimizedImageUrl("https://assets.auxodev.dk/assets/1a80aa53-8838-4db9-a7e2-061c5ee04d6a/158df1e9-8230-41a7-a84b-38b95d04c27f.png", 800, 85)} 
                          alt={language === 'da' ? "Nationalpark Sommercard Områdekort" : "Nationalpark Sommercard Area Map"} 
                          className="w-full h-auto object-cover transition-transform duration-500 group-hover/map:scale-105"
                          loading="lazy"
                        />
                        <div className="absolute inset-0 bg-black/0 group-hover/map:bg-black/10 transition-colors flex items-center justify-center">
                          <span className="bg-white/90 backdrop-blur-sm text-emerald-900 px-3 py-1.5 rounded-lg text-xs font-bold shadow-md opacity-0 group-hover/map:opacity-100 transition-opacity flex items-center gap-1.5">
                            <Maximize2 size={12} /> {language === 'da' ? 'Åbn kort i stort format' : 'Open map in large format'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Accessibility Icons Row (Moved after Highlights, Facilities & Distances, and made smaller) */}
              <div className="mb-12 py-6">
                <h4 className="text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-5">
                  {language === 'da' ? 'Tilgængelighed & Særlige Faciliteter' : 'Accessibility & Special Facilities'}
                </h4>
                <div className="grid grid-cols-3 sm:grid-cols-5 gap-x-4 gap-y-6 items-start">
                  {hotelAccessibilityIcons
                    .filter(icon => hotel.accessibilityIcons && hotel.accessibilityIcons.includes(icon.id))
                    .map((icon, index) => (
                      <HotelAccessibilityIcon key={icon.id} icon={icon} index={index} />
                    ))}
                </div>
              </div>

              {/* Amenities, Features & Distances */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
                <div className="bg-emerald-50/50 p-6 rounded-2xl border border-emerald-100">
                  <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                    <Check className="w-5 h-5 mr-2 text-emerald-600" />
                    {language === 'da' ? 'Højdepunkter' : 'Highlights'}
                  </h3>
                  <ul className="space-y-3">
                    {hotel.features.map((feature, index) => (
                      <li key={index} className="flex items-start text-gray-700 text-sm">
                        <Check className="w-4 h-4 mr-2.5 text-emerald-500 mt-0.5 flex-shrink-0" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                
                <div className="bg-gray-50 p-6 rounded-2xl border border-gray-100">
                  <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                    <Info className="w-5 h-5 mr-2 text-emerald-600" />
                    {language === 'da' ? 'Faciliteter' : 'Facilities'}
                  </h3>
                  <ul className="space-y-3">
                    {hotel.amenities.map((amenity, index) => (
                      <li key={index} className="flex items-start text-gray-700 text-sm">
                        <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full mr-2.5 mt-2 flex-shrink-0" />
                        <span>{amenity}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {hotel.distances && (
                  <div className="bg-stone-50 p-6 rounded-2xl border border-stone-200/60">
                    <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                      <MapPin className="w-5 h-5 mr-2 text-[#99A18C]" />
                      {language === 'da' ? 'Afstande' : 'Distances'}
                    </h3>
                    <ul className="space-y-3 text-sm text-gray-700">
                      <li className="flex items-center gap-2.5">
                        <Utensils className="w-4 h-4 text-[#99A18C] shrink-0" />
                        <div>
                          <span className="font-semibold block text-xs text-gray-500 uppercase">
                            {language === 'da' ? 'Restaurant' : 'Restaurant'}
                          </span>
                          <span>{hotel.distances.restaurant}</span>
                        </div>
                      </li>
                      <li className="flex items-center gap-2.5">
                        <MapPin className="w-4 h-4 text-[#99A18C] shrink-0" />
                        <div>
                          <span className="font-semibold block text-xs text-gray-500 uppercase">
                            {language === 'da' ? 'Centrum' : 'Center'}
                          </span>
                          <span>{hotel.distances.centrum}</span>
                        </div>
                      </li>
                      <li className="flex items-center gap-2.5">
                        <HeartPulse className="w-4 h-4 text-[#99A18C] shrink-0" />
                        <div>
                          <span className="font-semibold block text-xs text-gray-500 uppercase">
                            {language === 'da' ? 'Hospital' : 'Hospital'}
                          </span>
                          <span>{hotel.distances.hospital}</span>
                        </div>
                      </li>
                      <li className="flex items-center gap-2.5">
                        <Pill className="w-4 h-4 text-[#99A18C] shrink-0" />
                        <div>
                          <span className="font-semibold block text-xs text-gray-500 uppercase">
                            {language === 'da' ? 'Apotek' : 'Pharmacy'}
                          </span>
                          <span>{hotel.distances.apotek}</span>
                        </div>
                      </li>
                      <li className="flex items-center gap-2.5">
                        <Plane className="w-4 h-4 text-[#99A18C] shrink-0" />
                        <div>
                          <span className="font-semibold block text-xs text-gray-500 uppercase">
                            {language === 'da' ? 'Lufthavn' : 'Airport'}
                          </span>
                          <span>{hotel.distances.lufthavn}</span>
                        </div>
                      </li>
                      <li className="flex items-center gap-2.5">
                        <Waves className="w-4 h-4 text-[#99A18C] shrink-0" />
                        <div>
                          <span className="font-semibold block text-xs text-gray-500 uppercase">
                            {language === 'da' ? 'Strand / Sø' : 'Beach / Lake'}
                          </span>
                          <span>{hotel.distances.strand_soe}</span>
                        </div>
                      </li>
                    </ul>
                  </div>
                )}
              </div>

              {/* Dedicated Expandable Gallery Section */}
              <div className="mb-12">
                <div className="flex flex-col sm:flex-row sm:items-end justify-between mb-6 gap-4">
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 flex items-center">
                      <Grid className="w-6 h-6 mr-2 text-emerald-600" />
                      {language === 'da' ? 'Billedgalleri' : 'Image Gallery'}
                    </h3>
                    <p className="text-gray-600 mt-1 text-sm">
                      {language === 'da'
                        ? (hotel.gallery.length >= 10
                          ? `Gå på opdagelse i billederne fra ${hotel.name}. Visningen skifter til fuldt format uden beskæring, når du folder galleriet ud.`
                          : `Gå på opdagelse i billederne fra ${hotel.name}.`)
                        : (hotel.gallery.length >= 10
                          ? `Explore the images from ${hotel.name}. The view switches to full format without cropping when you expand the gallery.`
                          : `Explore the images from ${hotel.name}.`
                        )
                      }
                    </p>
                  </div>
                  {hotel.gallery.length > 8 && (
                    <button
                      onClick={() => setShowAllImages(!showAllImages)}
                      className="text-emerald-700 hover:text-emerald-800 font-bold text-sm transition-colors self-start sm:self-auto cursor-pointer"
                    >
                      {showAllImages 
                        ? (language === 'da' ? 'Vis færre billeder' : 'Show fewer images') 
                        : (language === 'da' ? `Vis alle ${hotel.gallery.length} billeder` : `Show all ${hotel.gallery.length} images`)
                      }
                    </button>
                  )}
                </div>

                {/* Conditional Layout: Masonry only when expanded AND hotel has 10+ images */}
                {showAllImages && hotel.gallery.length >= 10 ? (
                  /* Masonry Grid (No Cropping) */
                  <div className="columns-1 sm:columns-2 md:columns-3 lg:columns-4 gap-4 [column-fill:_balance] box-border">
                    {visibleImages.map((img, index) => (
                      <div
                        key={index}
                        onClick={() => openLightbox(index)}
                        className="break-inside-avoid group relative cursor-pointer overflow-hidden rounded-xl bg-stone-50 border border-stone-200/60 shadow-sm hover:shadow-md transition-all duration-300 mb-4 inline-block w-full"
                      >
                        <img
                          src={getOptimizedImageUrl(img, 600, 80)}
                          alt={`${hotel.name} galleri ${index + 1}`}
                          className="w-full h-auto object-contain rounded-xl transition-transform duration-500 group-hover:scale-[1.02]"
                          loading="lazy"
                          decoding="async"
                        />
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-300 flex items-center justify-center">
                          <span className="w-12 h-12 rounded-full bg-white/25 backdrop-blur-md text-white border border-white/30 shadow-lg flex items-center justify-center opacity-0 group-hover:opacity-100 transform scale-90 group-hover:scale-100 transition-all duration-300 hover:bg-white hover:text-emerald-950 hover:scale-110">
                            <Maximize2 className="w-5 h-5 stroke-[1.5]" />
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  /* Standard Clean Uniform Grid (Cropped to aspect ratio for a neat layout) */
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                    {visibleImages.map((img, index) => (
                      <div
                        key={index}
                        onClick={() => openLightbox(index)}
                        className="group relative aspect-[4/3] cursor-pointer overflow-hidden rounded-xl bg-stone-100 border border-stone-200/60 shadow-sm hover:shadow-md transition-all duration-300"
                      >
                        <img
                          src={getOptimizedImageUrl(img, 600, 80)}
                          alt={`${hotel.name} galleri ${index + 1}`}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                          loading="lazy"
                          decoding="async"
                        />
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-300 flex items-center justify-center">
                          <span className="w-12 h-12 rounded-full bg-white/25 backdrop-blur-md text-white border border-white/30 shadow-lg flex items-center justify-center opacity-0 group-hover:opacity-100 transform scale-90 group-hover:scale-100 transition-all duration-300 hover:bg-white hover:text-emerald-950 hover:scale-110">
                            <Maximize2 className="w-5 h-5 stroke-[1.5]" />
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {hotel.gallery.length > 8 && (
                  <div className="mt-6 text-center">
                    <button
                      onClick={() => setShowAllImages(!showAllImages)}
                      className="inline-flex items-center justify-center px-5 py-2.5 border border-emerald-600 text-emerald-700 hover:bg-emerald-50 font-bold text-sm rounded-xl transition-all cursor-pointer"
                    >
                      {showAllImages ? 'Vis færre billeder' : `Vis alle ${hotel.gallery.length} billeder`}
                    </button>
                  </div>
                )}
              </div>

              {/* Pricing Table Section */}
              <div className="mb-12">
                <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                  <Calendar className="w-6 h-6 mr-2 text-emerald-600" />
                  {language === 'da' ? 'Priser og Sæsoner' : 'Prices and Seasons'}
                </h3>
                <p className="text-gray-600 mb-6">
                  {language === 'da' 
                    ? 'Priserne herunder er vejledende fra-priser pr. person ved 2 voksne i et delt dobbeltværelse.' 
                    : 'The prices below are indicative from-prices per person based on 2 adults sharing a double room.'}
                  {hotel.destinationId === 'oestrig'
                    ? (language === 'da'
                      ? ' Prisen inkluderer hotelophold som kør-selv ferie (flyrejse er også muligt), evt. delvis forplejning og lokal bistand.'
                      : ' The price includes hotel stay as a self-drive holiday (flight is also possible), optional partial board, and local assistance.')
                    : (language === 'da'
                      ? ' Prisen inkluderer fly, hotelophold, evt. delvis forplejning og lokal bistand.'
                      : ' The price includes flights, hotel stay, optional partial board, and local assistance.')}
                </p>
                {hotel.mealPlan && (
                  <div className="mb-6 p-4 bg-emerald-50 border border-emerald-200 rounded-xl flex items-start gap-3 text-emerald-800">
                    <Utensils className="w-5 h-5 mt-0.5 text-emerald-600 flex-shrink-0" />
                    <span className="font-medium">{hotel.mealPlan}</span>
                  </div>
                )}
                
                {hotel.seasonalPrices && Object.keys(hotel.seasonalPrices).length > 0 && (
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
                    {Object.entries(hotel.seasonalPrices).map(([month, price]) => (
                      <div 
                        key={month} 
                        className="bg-emerald-50/40 border border-emerald-100/80 rounded-2xl p-5 text-center hover:bg-emerald-50/80 hover:border-emerald-200 hover:shadow-md transition-all duration-300 flex flex-col justify-between shadow-sm"
                      >
                        <span className="text-xs font-bold text-slate-500 uppercase tracking-wider block mb-3 capitalize">
                          {language === 'da' ? month : translateMonth(month, language)}
                        </span>
                        <div>
                          <span className="text-xl font-extrabold text-emerald-950 block leading-none">
                            {price}
                          </span>
                          <span className="text-[10px] font-bold text-emerald-700 uppercase tracking-wider block mt-1.5">
                            {language === 'da' ? 'pr. person' : 'per person'}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                <p className="mt-4 text-sm text-gray-500 italic">
                  {language === 'da'
                    ? '* Priserne varierer alt efter afrejsedag, flytilgængelighed og værelsestype. Kontakt os for et nøjagtigt tilbud.'
                    : '* Prices vary depending on departure date, flight availability, and room type. Contact us for an exact quote.'}
                </p>
              </div>
            </div>

            {/* Sidebar / Booking Card */}
            <div className="lg:col-span-1 min-w-0 w-full">
              <div className="sticky top-24 space-y-6">
                <div className="bg-white rounded-2xl border border-gray-200 shadow-xl overflow-hidden">
                  <div className="p-6 bg-emerald-900 text-white flex flex-col items-end text-right">
                    <p className="text-4xl font-bold leading-none">
                      <span className="text-lg font-normal text-emerald-200 mr-2">
                        {language === 'da' ? 'Fra' : 'From'}
                      </span>
                      {hotel.priceFrom.toLocaleString(language === 'da' ? 'da-DK' : 'en-US')},-
                    </p>
                    <p className="text-emerald-200 text-xs font-semibold uppercase tracking-wider mt-2">
                      {language === 'da' ? 'pr. pers.' : 'per person'}
                    </p>
                    <p className="text-emerald-100 text-xs mt-3 font-medium leading-relaxed text-right w-full">
                      {hotel.mealPlan 
                        ? hotel.mealPlan
                        : hotel.destinationId === 'oestrig'
                          ? (language === 'da' 
                            ? 'Inkluderer kør-selv (fly muligt), hotelophold, evt. delvis forplejning' 
                            : 'Includes self-drive (flight possible), hotel stay, optional partial board')
                          : (language === 'da' 
                            ? 'Inkluderer fly, hotelophold, evt. delvis forplejning' 
                            : 'Includes flights, hotel stay, optional partial board')}
                    </p>
                  </div>
                  <div className="p-6 space-y-6">
                    <p className="text-xs text-gray-500 italic leading-relaxed">
                      {language === 'da'
                        ? '* Prisen er en fra-pris og afhænger blandt andet af afrejsedato, rejseperiode, værelsestype, ledige pladser og eventuelle særlige behov. Kontakt os for et konkret tilbud på din rejse.'
                        : '* The price is a starting price and depends, among other things, on departure date, travel period, room type, available seats, and any special needs. Contact us for a concrete offer for your trip.'}
                    </p>
                    <div className="space-y-4">
                      <div className="flex items-center text-gray-700">
                        <MapPin className="w-5 h-5 mr-3 text-[#99A18C]" />
                        <span>{hotel.area}, {destination?.title}</span>
                      </div>
                    </div>
                    
                    <Link 
                      to={`${getLocalizedPath('quote', language)}?hotel=${encodeURIComponent(hotel.name)}&destination=${hotel.destinationId || ''}`} 
                      className="block w-full bg-[#99A18C] hover:bg-[#8a917e] text-white text-center py-4 rounded-xl font-bold transition-all shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
                    >
                      {language === 'da' ? 'Indhent tilbud' : 'Request quote'}
                    </Link>
                  </div>
                </div>

                {/* Newsletter / Contact Promo */}
                <div className="bg-stone-50 p-6 rounded-2xl border border-stone-200">
                  <h4 className="font-bold text-[#99A18C] mb-2">
                    {language === 'da' ? 'Har du spørgsmål?' : 'Have questions?'}
                  </h4>
                  <p className="text-stone-600 text-sm mb-4">
                    {language === 'da' ? (
                      <>Kontakt <strong>Alora</strong> på <strong>40 50 07 80</strong> eller skriv en mail for at høre mere om dette hotel.</>
                    ) : (
                      <>Contact <strong>Alora</strong> at <strong>+45 40 50 07 80</strong> or write an email to hear more about this hotel.</>
                    )}
                  </p>
                  <Link to={getLocalizedPath('contact', language)} className="text-[#99A18C] font-semibold text-sm hover:underline">
                    {language === 'da' ? 'Gå til kontakt →' : 'Go to contact →'}
                  </Link>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Lightbox Modal */}
      {selectedImage !== null && (
        <div 
          className="fixed inset-0 z-[100] bg-black/95 flex flex-col justify-between p-4 md:p-8 backdrop-blur-sm"
          onClick={closeLightbox}
        >
          {/* Top Bar */}
          <div className="flex justify-between items-center w-full text-white z-10">
            <span className="text-sm font-medium bg-black/40 px-3 py-1.5 rounded-full backdrop-blur-md">
              Billede {selectedImage + 1} af {hotel.gallery.length}
            </span>
            <button 
              className="text-white/80 hover:text-white transition-colors p-2 bg-black/40 hover:bg-black/60 rounded-full backdrop-blur-md cursor-pointer"
              onClick={closeLightbox}
              aria-label="Luk galleri"
            >
              <X size={24} />
            </button>
          </div>

          {/* Main Image Container */}
          <div className="relative flex-1 flex items-center justify-center my-4">
            <button 
              className="absolute left-2 md:left-4 text-white/70 hover:text-white transition-colors p-3 bg-black/40 hover:bg-black/60 rounded-full backdrop-blur-md z-10 cursor-pointer"
              onClick={prevImage}
              aria-label="Forrige billede"
            >
              <ChevronLeft size={28} />
            </button>

            <img 
              src={getOptimizedImageUrl(hotel.gallery[selectedImage], 1600, 85)} 
              alt={`${hotel.name} galleri stort ${selectedImage + 1}`}
              className="max-w-full max-h-[70vh] md:max-h-[75vh] object-contain shadow-2xl rounded-lg select-none transition-all duration-300"
              onClick={(e) => e.stopPropagation()}
            />

            <button 
              className="absolute right-2 md:right-4 text-white/70 hover:text-white transition-colors p-3 bg-black/40 hover:bg-black/60 rounded-full backdrop-blur-md z-10 cursor-pointer"
              onClick={nextImage}
              aria-label="Næste billede"
            >
              <ChevronRight size={28} />
            </button>
          </div>

          {/* Bottom Bar / Thumbnail Strip */}
          <div className="w-full flex flex-col items-center gap-4 z-10" onClick={(e) => e.stopPropagation()}>
            <p className="text-white/90 text-sm font-medium text-center max-w-xl">
              {hotel.name} — {hotel.area}
            </p>
            
            {/* Scrollable Thumbnail Strip */}
            <div className="w-full max-w-4xl hidden md:block">
              <div className="flex gap-2 overflow-x-auto py-2 px-4 justify-center scrollbar-thin scrollbar-thumb-white/20 scrollbar-track-transparent">
                {hotel.gallery.map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => setSelectedImage(idx)}
                    className={`relative flex-shrink-0 w-16 h-12 rounded-lg overflow-hidden border-2 transition-all cursor-pointer ${
                      selectedImage === idx ? 'border-emerald-500 scale-105 shadow-md' : 'border-transparent opacity-50 hover:opacity-100'
                    }`}
                  >
                    <img src={img} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HotelDetailPage;
